// components/Posts/PostHeader.jsx

import { Typography, Box } from '@mui/material';
import { Visibility, FavoriteBorder } from '@mui/icons-material';
import SafeChip from '../Common/SafeChip';
import UserNicknameWithEmoticon from '../Common/UserNicknameWithEmoticon';
import AuthorLink from '../Common/AuthorLink';
import { useUser } from '../../contexts/UserContext';

const PostHeader = ({ post }) => {
  const {
    title,
    category_name,    // ← 카테고리명
    nickname,
    main_title,
    sub_title,
    actual_author,
    created_at,
    is_anonymous,
    is_secret,
    equipped_emoticon,
    equipped_emoticon_text,
    like_status,
    view_count,
    is_edited,
  } = post;

  const { userInfo } = useUser();
  const isAdmin = ['admin', 'master'].includes(userInfo?.role);

  const formattedDate = new Date(created_at).toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

  // 작성자 fragment
  const nicknameFragment = is_anonymous ? (
    <Typography component="span" variant="body1" fontWeight="bold">
      작성자:&nbsp;
      {isAdmin ? `익명(${nickname})` : '익명'}
    </Typography>
  ) : (
    <Typography component="span" variant="body1" fontWeight="bold">
      작성자:&nbsp;
      <AuthorLink
        nickname={nickname || actual_author || '알 수 없음'}
        disabled={false}
        sx={{ fontWeight: 'bold', textDecoration: 'none' }}
      />
    </Typography>
  );

  return (
    <Box sx={{ mb: 2 }}>
      {/* 카테고리 텍스트 */}
      {category_name && (
        <Typography
          variant="h5"           // 더 큰 텍스트 (또는 'body1', 'body2', 'caption' 등으로 조정 가능)
          sx={{
            mb: 1,
            textAlign: 'left',
            fontWeight: 'bold',   // 굵게
            color: 'primary.main' // 파란 계열. 또는 '#333', 'text.primary', 'error.main' 등
          }}
        >
          [{category_name}]
        </Typography>
      )}

      {/* 제목 */}
      <Typography variant="h5" sx={{ textAlign: 'left' }}>
        {title}
      </Typography>

      {/* 작성자, 익명/비밀 칩 */}
      <Box sx={{ width: '100%', mt: 0.5, mb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <UserNicknameWithEmoticon
            username=""
            usernameComponent={nicknameFragment}
            mainTitle={is_anonymous ? null : main_title}
            subTitle={is_anonymous ? null : sub_title}
            emoticonUrl={is_anonymous ? null : equipped_emoticon}
            emojiText={is_anonymous ? null : equipped_emoticon_text}
            size={30}
          />

          {is_anonymous && (
            <SafeChip label="익명" color="default" variant="outlined" size="small" />
          )}
          {is_secret && (
            <SafeChip label="비밀글" color="warning" variant="outlined" size="small" />
          )}
        </Box>
      </Box>

      {/* 작성일 + 수정 표시 */}
      <Typography
        variant="subtitle2"
        color="text.secondary"
        sx={{ mt: 0.5, textAlign: 'left', width: '100%' }}
      >
        작성일: {formattedDate}
        {is_edited && ' (수정됨)'}
      </Typography>

      {/* 조회수 & 좋아요 */}
      <Box
        sx={{
          mt: 0.3,
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          textAlign: 'left',
          pl: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Visibility fontSize="small" sx={{ color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary">
            {view_count ?? 0}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <FavoriteBorder fontSize="small" sx={{ color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary">
            {like_status?.like_count ?? 0}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default PostHeader;
